import React, { useEffect, useState } from 'react';

import { useCountdown } from '#usehooks-ts';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import {
  cookieJar,
  trackApplyEvent,
  uiRoutes,
  useSendEmailCode,
  useVerifyEmailCode,
} from '@consumer-frontend/nc-api';
import {
  initialAlertBannerProps,
  somethingWentWrongAlertBannerProps,
  verificationCodeValidationMessages,
  verificationCodeValidations,
  VerifyEmailCodeFormT,
} from '@consumer-frontend/nc-utils';
import { Container, Fieldset } from '@consumer-frontend/shared-ui';

import { NcExpiredCodeErrorMessage } from './NcExpiredCodeErrorMessage';
import { NcSendCodeLinkCountdown } from './NcSendCodeLinkCountdown';
import { useApplyImmediately, useApplyRouting } from '../../routing';
import {
  alertBannerAtom,
  incomeDataAtom,
  registrationDataAtom,
  sendCodeCountdownSecondsAtom,
} from '../../store';
import { NcButton } from '../NcButton';
import { NcForm } from '../NcForm';
import { NcInput } from '../NcInput';
import { NcPageWithAlertBanner } from '../NcPageWithAlertBanner';

import '@consumer-bootstrap/netcredit.scss';
// eslint-disable-next-line import/order
import '@consumer-bootstrap/brands/netcredit/apply/verify-email-code-page.scss';

export const NcVerifyEmailCodePage: React.FC = () => {
  const sendCodeCountdownSeconds = useAtomValue(sendCodeCountdownSecondsAtom);
  const { email } = useAtomValue(registrationDataAtom);
  const { handleBack, handleContinue } = useApplyRouting();
  const { applyImmediately } = useApplyImmediately();
  const navigate = useNavigate();
  const setAlertBanner = useSetAtom(alertBannerAtom);
  const setRegistrationData = useSetAtom(registrationDataAtom);
  const setIncomeData = useSetAtom(incomeDataAtom);
  const {
    error: verifyEmailCodeError,
    isMutating,
    verifyEmailCode,
  } = useVerifyEmailCode();
  const [showExpiredCodeError, setShowExpiredCodeError] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const { error, sendEmailCode } = useSendEmailCode();
  const [count, { startCountdown, resetCountdown }] = useCountdown({ countStart: sendCodeCountdownSeconds });
  const {
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
    reset: resetForm,
    setError,
  } = useForm<VerifyEmailCodeFormT>();

  const resetPage = () => {
    resetCountdown();
    startCountdown();
    resetForm();
    setShowExpiredCodeError(false);
    setAlertBanner(initialAlertBannerProps);
  };

  const onSubmitValid: SubmitHandler<VerifyEmailCodeFormT> = async ({ verificationCode }) => {
    setShowExpiredCodeError(false);
    setAlertBanner(initialAlertBannerProps);

    const response = await verifyEmailCode({ code: verificationCode.toUpperCase() });

    if (!response) {
      return; // Error handled below
    }

    setIsRedirecting(true);

    if (response.token?.accessToken && response.token?.refreshToken) {
      cookieJar.saveTokens(response.token.accessToken, response.token.refreshToken);

      if (await applyImmediately()) { return; }
    }

    await handleContinue({ emailCodeHasToken: !!response.token?.accessToken });
  };

  const onSubmitInvalid: SubmitErrorHandler<VerifyEmailCodeFormT> = ({ verificationCode }) => {
    if (verificationCode?.type === 'pattern') {
      trackApplyEvent('incorrectCodeEntered', { source: 'clientValidation' });
    }
  };

  const handleVerifyAnotherWay = () => {
    trackApplyEvent('verifyAnotherWayClicked');
    handleBack();
  };

  const handleSendCode = async (source: 'countdownLink' | 'expiredCodeError' = 'countdownLink'): Promise<void> => {
    trackApplyEvent('sendNewCodeClicked', { source });
    resetPage();
    await sendEmailCode();
  };

  // Display inline field error when the submitted code is incorrect (401)
  useEffect(
    () => {
      if (verifyEmailCodeError?.response?.status === 401) {
        setError('verificationCode', { message: verificationCodeValidationMessages.pattern });
        trackApplyEvent('incorrectCodeEntered', { source: 'api' });
      }
    },
    [verifyEmailCodeError, setError],
  );

  // Display an inline field error when the user has exceeded the maximum number of attempts (429), code is expired (400) or not found (404)
  useEffect(
    () => {
      const status = verifyEmailCodeError?.response?.status;

      if (status === 429 || status === 400 || status === 404) {
        setShowExpiredCodeError(true);
        trackApplyEvent('codeExpired', { status });
      }
    },
    [verifyEmailCodeError],
  );

  // Display a generic server error banner at the top of the page (500)
  useEffect(
    () => {
      if (verifyEmailCodeError?.response?.status === 500) {
        setAlertBanner(somethingWentWrongAlertBannerProps);
      }
    },
    [verifyEmailCodeError, setAlertBanner],
  );

  // treat network failures (no response) the same as a server error
  useEffect(
    () => {
      if (verifyEmailCodeError && !verifyEmailCodeError.response) {
        setAlertBanner(somethingWentWrongAlertBannerProps);
      }
    },
    [verifyEmailCodeError, setAlertBanner],
  );

  // On cache miss (422), route back to the Basic Information page
  useEffect(
    () => {
      if (verifyEmailCodeError?.response?.status === 422) {
        navigate(uiRoutes.apply.basicInfo);
      }
    },
    [
      verifyEmailCodeError,
      navigate,
      setIncomeData,
      setRegistrationData,
    ],
  );

  // Start countdown on initial page render
  useEffect(() => {
    startCountdown();
  }, [startCountdown]);

  // Throw unexpected errors to the error boundary; 400, 401, 404, 422, 429, 500 and
  // network errors (no response) are all handled inline or via navigation above.
  if (verifyEmailCodeError && verifyEmailCodeError.response && ![
    400,
    401,
    404,
    422,
    429,
    500,
  ].includes(verifyEmailCodeError.response.status)) {
    throw verifyEmailCodeError;
  }

  // Sets status and alert when sendEmailCode has an error
  useEffect(() => {
    if (error?.response?.status === 422) {
      navigate(uiRoutes.apply.basicInfo);
    } else if (error) {
      setAlertBanner(somethingWentWrongAlertBannerProps);
    }
  }, [
    error,
    navigate,
    setAlertBanner,
  ]);

  const isSubmissionDisabled = isMutating || isSubmitting || isRedirecting;

  return (
    <NcPageWithAlertBanner>
      <Container className="nc-verify-email-code-page" hasBoxShadow>
        <h1 className="h3">Enter Your Code</h1>
        <p className="subheading">
          Enter the verification code we sent to <strong>{email}</strong>.
          {' '}
          {/* eslint-disable-next-line react/jsx-curly-brace-presence */}
          {"If you don't see an email in your inbox after a few minutes, please check your spam folder."}
        </p>

        <NcForm onSubmit={handleSubmit(onSubmitValid, onSubmitInvalid)}>
          <NcInput
            {...register('verificationCode', verificationCodeValidations)}
            autoComplete="one-time-code"
            errorMessage={
              showExpiredCodeError
                ? <NcExpiredCodeErrorMessage handleSendCode={() => handleSendCode('expiredCodeError')} />
                : errors.verificationCode?.message
            }
            id="verification-code"
            label="Verification Code"
            type="text"
          />

          <Fieldset className="cta-buttons">
            <NcButton
              disabled={isSubmissionDisabled}
              type="submit"
              variant="primary"
            >
              Continue
            </NcButton>

            <NcButton
              disabled={isSubmissionDisabled}
              type="button"
              variant="outline-primary"
              onClick={handleVerifyAnotherWay}
            >
              Verify Another Way
            </NcButton>

            <NcSendCodeLinkCountdown count={count} handleSendCode={() => handleSendCode('countdownLink')} />
          </Fieldset>
        </NcForm>
      </Container>
    </NcPageWithAlertBanner>
  );
};
